import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Controller } from "react-hook-form";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import Select from 'react-select';
import ReactCountryFlag from 'react-country-flag';

const incidentTypes = [
  'ALARM CALLS', 'BOMB THREAT', 'BREAK IN / ATTEMPTED', 'COUNTERFEIT CURRENCY',
  'CUSTOMER DISTURBANCE', 'GIFT CARD FRAUD', 'LEWD AND LASCIVIOUS BEHAVIOR',
  'PHONE SCAM', 'POWER OUTAGE', 'QUICK CHANGE SCAM', 'ROBBERY', 'OTHER',
];

const numericRegex = /^[0-9]+$/;

export default function LossPreventionForm({ onReview, goBack, defaultValues }) {
  const store_id = sessionStorage.getItem("store_id")
  const manager = sessionStorage.getItem("first_name");

  const { register, handleSubmit, watch, formState: { errors }, control, setValue } = useForm({
    defaultValues: {
      store_id,
      reported_by: manager,
      witness_country_code: '+91',
      ...defaultValues,
      incident_date: defaultValues?.incident_date ? new Date(defaultValues.incident_date) : null,
      incident_time: defaultValues?.incident_time ? new Date(defaultValues.incident_time) : null,
      reported_date: defaultValues?.reported_date ? new Date(defaultValues.reported_date) : new Date(),
    }
  });

  const selectedIncident = watch("incident_type");
  const authoritiesCalled = watch("authoritiesCalled");
  const witness = watch('witness');
  const selectedWitnessCountryCode = watch("witness_country_code");

  const selectedCountryCode = watch("country_code");

  const countryCodes = useMemo(() => {
    const countries = getCountries();
    const countryData = countries.map(country => {
      const callingCode = `+${getCountryCallingCode(country)}`;

      const maxDigitsMap = {
        'US': 10, 'CA': 10, 'IN': 10, 'GB': 10, 'AU': 9,
        'JP': 10, 'CN': 11, 'DE': 11, 'FR': 9, 'IT': 10, 'ES': 9
      };

      return {
        code: callingCode,
        name: country,
        country: country,
        maxDigits: maxDigitsMap[country] || 10
      };
    });

    return countryData.sort((a, b) => {
      const numA = parseInt(a.code.replace('+', ''));
      const numB = parseInt(b.code.replace('+', ''));
      return numA - numB;
    });
  }, []);

  const getMaxDigits = (code) => {
    const country = countryCodes.find(c => c.code === code);
    return country ? country.maxDigits : 10;
  };

  const formatDataForSubmission = (data) => {
    const formattedData = { ...data };

    if (formattedData.witness === 'Yes' && formattedData.customer_phone) {
      const { country_code, customer_phone, ...rest } = formattedData;
      // preserve the original country_code and customer_phone so the form
      // can be restored if the user navigates back from Preview
      return {
        ...rest,
        country_code,
        customer_phone,
        witness_phone: `${country_code}${customer_phone}`,
      };
    }

    return formattedData;
  };

  const onSubmit = data => {
    const finalData = formatDataForSubmission(data);
    console.log("Submit Data:", finalData);
  };

  const handleReview = (data) => {
    const finalData = formatDataForSubmission(data);
    onReview(finalData);
  };


  return (
    <div className=" mx-auto p-6 bg-white shadow rounded space-y-6 w-full ">
      <h2 className="text-2xl font-bold mb-4">Loss Prevention Incident Report</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Store Number */}
        <div>
          <label className="block font-medium mb-1">Store Number</label>
          <input {...register("store_id")} className="border border-gray-300 rounded px-4 py-2 w-full focus:outline-none focus:ring-2 focus:ring-orange-500" value={store_id}
          />

        </div>

        {/* Incident Type */}
        <div>
          <label className="block font-medium mb-1">Incident Type</label>
          <select {...register("incident_type", { required: "Select incident type" })}
            className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500">
            <option value="">-- Select --</option>
            {["Alarm Call", "Bomb Threat", "Cash Theft", "Counterfeit", "Customer Disturbance", "Giftcard Fraud", "Phone Scam", "Power Outage", "Quick Change Artist", "Robbery", "Other"].map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          {errors.incident_type && <p className="text-red-500 text-sm mt-1">{errors.incident_type.message}</p>}
        </div>

        {selectedIncident === "Other" && (
          <div>
            <label className="block font-medium mb-1">Other</label>
            <input {...register("other", { required: "Enter other details" })}
              className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500" />
            {errors.other && <p className="text-red-500 text-sm mt-1">{errors.other.message}</p>}
          </div>)
        }

        {/* Incident Date */}
        <div>
          <label className="block font-medium mb-1">Incident Date</label>

          <Controller
            control={control}
            name="incident_date"
            rules={{ required: "Select Incident Date" }}
            render={({ field }) => (
              <DatePicker
                {...field}
                selected={field.value}
                onChange={(val) => field.onChange(val)}
                dateFormat="dd-MMM-yyyy"
                maxDate={new Date()}
                placeholderText="Select date"
                className="w-full border border-gray-300 rounded px-4 py-2 me-22 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            )}
          />

          {errors.incident_date && (
            <p className="text-red-500 text-sm mt-1">
              {errors.incident_date.message}
            </p>
          )}
        </div>


        <div>
          <label className="block font-medium mb-1">Incident Time</label>

          <Controller
            control={control}
            name="incident_time"
            rules={{ required: "Select Incident Time" }}
            render={({ field }) => (
              <DatePicker
                {...field}
                selected={field.value}
                onChange={(val) => field.onChange(val)}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="h:mm aa"
                placeholderText="Select time"
                className="w-full border border-gray-300 rounded px-4 py-2 me-22 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            )}
          />

          {errors.incident_time && (
            <p className="text-red-500 text-sm mt-1">
              {errors.incident_time.message}
            </p>
          )}
        </div>


        {/* Authorities Called */}
        <div className="col-span-2">
          <label className="block font-medium mb-1">Is Authorities (Police, Fire, Ambulance) Called?</label>
          <select {...register("authoritiesCalled", { required: "Select an option" })}
            className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500">
            <option value="">-- Select --</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
          {errors.authoritiesCalled && <p className="text-red-500 text-sm mt-1">{errors.authoritiesCalled.message}</p>}
        </div>

        {authoritiesCalled === "Yes" && (
          <>
            <div>
              <label className="block font-medium mb-1">Responding Authority Name</label>
              <input {...register("responding_authority", { required: "Enter authority name" })}
                className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500" />
              {errors.responding_authority && <p className="text-red-500 text-sm mt-1">{errors.responding_authority.message}</p>}
            </div>

            <div>
              <label className="block font-medium mb-1">Report Number</label>
              <input {...register("report_number", { required: "Enter report number" })}
                className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500" />
              {errors.report_number && <p className="text-red-500 text-sm mt-1">{errors.report_number.message}</p>}
            </div>
          </>
        )}


        {/* Incident Description */}
        <div className="col-span-2">
          <label className="block font-medium mb-1">Describe What Occurred</label>
          <textarea {...register("incident_description", { required: "Description is required", maxLength: { value: 750, message: "Maximum 750 characters allowed" } })}
            rows={4}
            maxLength={750}
            className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500" />
          {errors.incident_description && <p className="text-red-500 text-sm mt-1">{errors.incident_description.message}</p>}
        </div>

        {/* Witness */}
        <div className="col-span-2">
          <label className="block font-medium mb-1">Witness Present?</label>
          <select {...register("witness", { required: "Select an option" })}
            className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="">-- Select --</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
          {errors.witness && <p className="text-red-500 text-sm mt-1">{errors.witness.message}</p>}
        </div>

        {witness === "Yes" && (
          <>
            <div>
              <label className="block font-medium mb-1">Witness Name</label>
              <input {...register("witness_name", { required: "Name is required" })}
                className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500" />
              {errors.witness_name && <p className="text-red-500 text-sm mt-1">{errors.witness_name.message}</p>}
            </div>

            <div>
              <label className="block font-medium mb-1">Witness Email</label>
              <input {...register("witness_email", {
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Invalid email address"
                }
              })}
                className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500" />
              {errors.witness_email && <p className="text-red-500 text-sm mt-1">{errors.witness_email.message}</p>}
            </div>

            <div>
              <label className="block mb-1 font-medium">Witness Phone</label>
              <div className="flex gap-2 items-center">
                <Controller
                  name="country_code"
                  control={control}
                  defaultValue={"+91"}
                  rules={{ required: "Country code is required" }}
                  render={({ field }) => {
                    const options = countryCodes.map((country) => ({
                      value: country.code,
                      label: `${country.country} (${country.code})`,
                      country: country.country,
                    }));

                    const selectedOption =
                      options.find((opt) => opt.value === field.value) ||
                      options.find((opt) => opt.value === "+91") ||
                      options[0];

                    return (
                      <div className="w-[240px]">
                        <Select
                          options={options}
                          value={selectedOption}
                          onChange={(selected) => field.onChange(selected.value)}
                          placeholder="Search country..."
                          isSearchable
                          getOptionLabel={(option) => (
                            <div className="flex items-center gap-2">
                              <ReactCountryFlag
                                countryCode={option.country}
                                svg
                                style={{
                                  width: "1.2em",
                                  height: "1.2em",
                                  borderRadius: "2px",
                                  objectFit: "cover",
                                }}
                              />
                              <span>{option.country}</span>
                              <span className="text-gray-500 ml-auto">{option.value}</span>
                            </div>
                          )}
                          filterOption={(option, inputValue) => {
                            const searchText = inputValue.toLowerCase();
                            return (
                              option.data.country.toLowerCase().includes(searchText) ||
                              option.data.value.includes(searchText)
                            );
                          }}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              minHeight: "42px",
                              borderRadius: "0.5rem",
                              borderColor: state.isFocused ? "#EF7320" : "#d1d5db",
                              boxShadow: state.isFocused
                                ? "0 0 0 2px rgba(239,115,32,0.2)"
                                : "none",
                              "&:hover": { borderColor: "#EF7320" },
                              backgroundColor: "white",
                              cursor: "pointer",
                            }),
                            option: (base, { isFocused, isSelected }) => ({
                              ...base,
                              backgroundColor: isSelected
                                ? "#EF7320"
                                : isFocused
                                  ? "#FCE8D6"
                                  : "white",
                              color: isSelected ? "white" : "#111827",
                              cursor: "pointer",
                              fontSize: "0.875rem",
                            }),
                            singleValue: (base) => ({
                              ...base,
                              color: "#111827",
                              fontWeight: "400",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }),
                          }}
                        />
                      </div>
                    );
                  }}
                />


                <Controller
                  name="customer_phone"
                  control={control}
                  rules={{
                    required: "Phone Number is required",
                    validate: (value) => {
                      const maxDigits = getMaxDigits(selectedCountryCode);
                      if (value && value.length !== maxDigits) {
                        return `Phone number must be exactly ${maxDigits} digits`;
                      }
                      return true;
                    },
                    pattern: {
                      value: numericRegex,
                      message: "Only numbers are allowed",
                    },
                  }}
                  render={({ field }) => (
                    <input
                      value={field.value || ''}
                      onChange={(e) => {
                        const filteredValue = e.target.value.replace(/[^0-9]/g, '');
                        const maxDigits = getMaxDigits(selectedCountryCode);
                        if (filteredValue.length <= maxDigits) {
                          field.onChange(filteredValue);
                        }
                      }}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      placeholder={`Enter ${getMaxDigits(selectedCountryCode)} digits`}
                      className="border border-gray-300 rounded px-4 py-2 w-full focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  )}
                />
              </div>
              {errors.customer_phone && <p className="text-red-500 text-sm">{errors.customer_phone.message}</p>}
            </div>
          </>
        )}

      </div>
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4'>
        <div >
          <label className="block font-medium mb-1">Reported By</label>
          <input {...register("reported_by")} className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500" {...register("reported_by", { required: "fill the reported by" })}
            value={manager} />
          {errors.reported_by && <p className="text-red-500 text-sm mt-1">{errors.reported_by.message}</p>}
        </div>

        <div>
          <label className="block font-medium mb-1">Report Date</label>

          <Controller
            control={control}
            name="reported_date"
            defaultValue={new Date()}
            rules={{ required: "Select Reported Date" }}
            render={({ field }) => (
              <DatePicker
                {...field}
                selected={field.value}
                onChange={(val) => field.onChange(val)}
                dateFormat="dd-MMM-yyyy"
                disabled
                className="w-full border border-gray-300 rounded px-4 py-2 me-22 bg-gray-100 cursor-not-allowed"
                placeholderText="Select date"
              />
            )}
          />


          {errors.reported_date && (
            <p className="text-red-500 text-sm mt-1">
              {errors.reported_date.message}
            </p>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 mt-6">
        <button type="button" onClick={() => handleSubmit(handleReview)()} className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700">
          Preview
        </button>
        <button type="button" onClick={goBack} className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600">
          Back
        </button>
      </div>
    </div>

  );
}